'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useQuality } from '@/components/shared/QualityProvider';
import { projects, type Project } from '@/data/projects';
import { getFeature, getProjectFeatures } from '@/data/features';
import { useJourneyOrder } from '@/lib/journeyOrder';
import { FEED_REVEAL } from '@/lib/motionTokens';
import type { Locale } from '@/i18n/routing';

// One IntersectionObserver per card, disconnected once revealed, so the
// cost stays flat and there is no GSAP / scroll-linked work. One-shot —
// scrolling back up doesn't re-trigger, which avoids flicker on long
// lists. Under reduced-motion (or where IntersectionObserver is missing)
// the card is shown immediately.
//
// `staggerMs` defers the `shown` flip after intersection, used by callers
// to cascade adjacent cards instead of revealing them in unison. Reduced-
// motion bypasses the stagger so the page is instantly readable.
function useReveal<T extends HTMLElement>(staggerMs = 0) {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduced || typeof IntersectionObserver === 'undefined') {
      // Show immediately when the user has prefers-reduced-motion OR when
      // the runtime has no IntersectionObserver. One-shot setState in the
      // effect is the right pattern here — the alternative branch would
      // need SSR-aware render-phase logic with no real benefit.
       
      setShown(true);
      return;
    }
    let timer: number | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          timer = window.setTimeout(() => setShown(true), staggerMs);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timer !== undefined) window.clearTimeout(timer);
    };
    // `staggerMs` is derived from a stable `index` (card key never moves),
    // so capturing it at mount is correct — and a fixed-size deps array
    // keeps Fast Refresh from tripping the "deps changed size" guard when
    // this hook's signature was edited mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, shown };
}

// Each revealed element descends into place from just above (top-to-
// bottom) with a fade — no horizontal movement, no scale. The per-element
// `transition-delay` staggers the description in (image first, then the
// copy cascades number → title → role → pitch → outcome → chips → CTA).
// Delay is irrelevant before `shown` flips, so it's always set.
//
// NOTE: in Tailwind v4 `translate-*` sets the individual `translate` CSS
// property, NOT the `transform` shorthand — so the transition MUST list
// it explicitly or the movement jumps instantly and only opacity fades.
const REVEAL_TRANSITION = 'transition-[opacity,transform,translate] duration-700 ease-out';

function revealClass(shown: boolean) {
  return shown
    ? `${REVEAL_TRANSITION} translate-y-0 opacity-100`
    : `${REVEAL_TRANSITION} -translate-y-4 opacity-0`;
}

// The image is large, so a 16px shift reads as static — it gets a bigger
// descent so it clearly moves in like the copy beside it.
function imageRevealClass(shown: boolean) {
  return shown
    ? `${REVEAL_TRANSITION} translate-y-0 opacity-100`
    : `${REVEAL_TRANSITION} -translate-y-6 opacity-0`;
}

/**
 * Responsive project feed. Used on `/grid` for every tier and on the home
 * page's mobile path via `mobileOnly`. Native scroll, no GSAP.
 *
 *   • narrow → stacked card (screenshot over text)
 *   • lg+    → alternating split row (image one side, text the other)
 *
 * On reveal the image leads and the description lines stagger in beneath
 * it (see `useReveal` + `revealClass`).
 */
export function ProjectFeed({ mobileOnly = false }: { mobileOnly?: boolean }) {
  const { tier } = useQuality();
  const locale = useLocale() as Locale;
  const t = useTranslations('Grid');

  // Same per-visit shuffle the journey uses — keeps `/` (low tier),
  // `/grid` and the desktop journey sidebar all in sync within a session.
  // Canonical order on SSR + first paint, shuffled after hydration.
  // Hook MUST come before any conditional return so React's
  // rules-of-hooks invariant holds across the mobileOnly short-circuit
  // below — otherwise the order of hooks differs between renders.
  const order = useJourneyOrder();
  const ordered = order.map((idx) => projects[idx]);

  // Home mounts this inside MobileIntroGate and only wants it on mobile;
  // /grid mounts it for everyone. Mirrors the old MobilePortfolioList gate.
  if (mobileOnly && tier !== 'low') return null;

  return (
    <section className="mx-auto w-full max-w-[1280px] px-5 pt-4 pb-16 sm:px-6 sm:pt-6 lg:px-8 3xl:max-w-[1400px]">
      <ul className="project-feed flex flex-col gap-28 sm:gap-32 lg:gap-32 3xl:gap-40">
        {ordered.map((project, i) => (
          <FeedCard
            key={project.slug}
            project={project}
            index={i}
            locale={locale}
            visitLabel={t('visitLive')}
          />
        ))}
      </ul>
    </section>
  );
}

function FeedCard({
  project,
  index,
  locale,
  visitLabel,
}: {
  project: Project;
  index: number;
  locale: Locale;
  visitLabel: string;
}) {
  // Cascade adjacent cards on first reveal. The cap means anything past
  // `staggerMaxIndex` inherits the same lead-in (no draggy delay after
  // the user has scrolled deep into the feed).
  const staggerMs =
    Math.min(index, FEED_REVEAL.staggerMaxIndex) * FEED_REVEAL.staggerStepMs;
  const { ref, shown } = useReveal<HTMLLIElement>(staggerMs);
  const numberLabel = String(index + 1).padStart(2, '0');
  const imageOnRight = index % 2 === 1;

  return (
    <li
      ref={ref}
      className="project-feed__card grid grid-cols-1 items-center gap-6 lg:grid-cols-12 lg:gap-12"
    >
      <a
        href={project.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${project.name} — ${project.role[locale]}`}
        className={`group block lg:col-span-8 ${imageOnRight ? 'lg:order-2' : ''} ${imageRevealClass(shown)}`}
      >
        <div
          className="relative aspect-[16/10] w-full overflow-hidden rounded-sm"
          style={{ boxShadow: `0 0 0 1px ${project.color}55` }}
        >
          {/* next/image hands srcset + AVIF/WebP conversion + CLS-safe
              intrinsic sizing for free. `sizes` mirrors the lg:col-span-8
              vs full-width breakpoint above so the browser picks the
              correct render width on each viewport. */}
          <Image
            src={project.screenshot}
            alt={project.name}
            width={1440}
            height={900}
            sizes="(min-width: 1024px) 66vw, 100vw"
            className="project-feed__screenshot h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-[1.02]"
            draggable={false}
          />
        </div>
      </a>

      <div className={`lg:col-span-4 ${imageOnRight ? 'lg:order-1' : ''}`}>
        <header>
          <p
            className={`font-mono text-xs uppercase tracking-[3px] text-(--color-muted-foreground) ${revealClass(shown)}`}
            style={{ transitionDelay: `${60}ms` }}
          >
            <span style={{ color: project.color }}>{numberLabel}</span>
            <span aria-hidden="true" className="mx-2 opacity-40">·</span>
            {project.year}
          </p>
          <h2
            className={`mt-2 text-3xl font-extrabold uppercase leading-[0.95] tracking-tight text-(--color-muted) sm:text-4xl 3xl:text-5xl ${revealClass(shown)}`}
            style={{ transitionDelay: `${120}ms` }}
          >
            {project.name}
          </h2>
          <p
            className={`mt-1.5 font-mono text-xs uppercase tracking-[3px] text-(--color-muted-foreground) ${revealClass(shown)}`}
            style={{ transitionDelay: `${180}ms` }}
          >
            {project.role[locale]}
          </p>
        </header>

        <p
          className={`mt-4 text-base leading-relaxed text-(--color-muted)/85 lg:text-lg ${revealClass(shown)}`}
          style={{ transitionDelay: `${260}ms` }}
        >
          {project.pitch[locale]}
        </p>

        {project.outcome && (
          <p
            className={`mt-2 font-mono text-sm uppercase tracking-[3px] ${revealClass(shown)}`}
            style={{ color: project.color, transitionDelay: `${320}ms` }}
          >
            {project.outcome[locale]}
          </p>
        )}

        <div
          className={`mt-4 flex flex-wrap gap-1.5 ${revealClass(shown)}`}
          style={{ transitionDelay: `${400}ms` }}
        >
          {getProjectFeatures(project.slug).map((key) => {
            const meta = getFeature(key);
            return (
              <span
                key={key}
                title={meta?.description[locale]}
                className="border px-2 py-1 font-mono text-[10px] uppercase tracking-[2px] text-(--color-muted)/80"
                style={{ borderColor: `${project.color}55` }}
              >
                {meta?.label[locale] ?? key}
              </span>
            );
          })}
        </div>

        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`project-feed__cta group/cta mt-6 inline-flex w-fit items-center gap-4 py-1 ${revealClass(shown)}`}
          style={{ transitionDelay: `${480}ms` }}
        >
          <span
            aria-hidden
            className="block h-9 w-[3px] transition-[height] duration-500 ease-out group-hover/cta:h-14"
            style={{ backgroundColor: project.color }}
          />
          <span className="flex items-center gap-3 font-mono text-base uppercase tracking-[4px] text-(--color-muted) underline decoration-1 underline-offset-[6px]">
            {visitLabel}
            <span
              aria-hidden
              className="no-underline text-xl leading-none transition-transform duration-500 ease-out group-hover/cta:translate-x-1"
              style={{ color: project.color }}
            >
              →
            </span>
          </span>
        </a>
      </div>
    </li>
  );
}
